/**
 * 续航计算统一入口
 *
 * 根据设备 manifest（MCU / sensor / microSwitch / encoder / battery）自动路由到对应的续航表。
 * 各芯片续航表放在独立文件中（如 batteryLifeN54L15.ts、batteryLifeN54H20.ts 等）。
 */
import type {
  ReportRate,
  SensorMode,
  RfBoostSwitch,
  KeyScanRate,
  RfPacketType,
} from '@rapoo/device-sdk'
import { getReportRateSlot } from '../../composables/usePerformance'
import { useDeviceStore } from '@/stores'

// ===================== L15 (零售版, MCU=N54L15) =====================
import {
  getBatteryLife_l15_pt0_3398_kt0_rt0_800,
  getBatteryLife_l15_pt1_3398_kt0_rt0_800,
  getBatteryLife_l15_pt0_3950_kt1_rt0_800,
  getBatteryLife_l15_pt1_3950_kt1_rt0_800,
  getBatteryLife_l15_pt0_3950_kt0_rt0_800,
  getBatteryLife_l15_pt1_3950_kt0_rt0_800,
  getBatteryLife_l15_pt0_3955_kt1_rt1_800,
  getBatteryLife_l15_pt1_3955_kt1_rt1_800,
  getBatteryLife_l15_pt0_3950_kt1_rt1_300,
  getBatteryLife_l15_pt1_3950_kt1_rt1_300,
} from './batteryLifeN54L15'

// ===================== H20 (零售版, MCU=N54H20) =====================
import {
  getBatteryLife_h20_pt0_3950_kt1_rt1_800,
  getBatteryLife_h20_pt1_3950_kt1_rt1_800,
  getBatteryLife_h20_pt0_3955_kt1_rt1_800,
  getBatteryLife_h20_pt1_3955_kt1_rt1_800,
  getBatteryLife_h20_pt0_3950_kt1_rt1_300,
  getBatteryLife_h20_pt1_3950_kt1_rt1_300,
} from './batteryLifeN54H20'

// ===================== L05 (网吧版, MCU=N54L05) =====================
import {
  getBatteryLife_l05_pt0_3398_kt0_rt0_800,
  getBatteryLife_l05_pt1_3398_kt0_rt0_800,
  getBatteryLife_l05_pt0_3950_kt1_rt0_800,
  getBatteryLife_l05_pt1_3950_kt1_rt0_800,
  getBatteryLife_l05_pt0_3950_kt0_rt0_800,
  getBatteryLife_l05_pt1_3950_kt0_rt0_800,
} from './batteryLifeN54L05'

// ===================== LM20 (零售版, MCU=N54LM20) =====================
// 调用L15
// import {
//   getBatteryLife_lm20_pt0_3955_kt1_rt1_800,
//   getBatteryLife_lm20_pt1_3955_kt1_rt1_800,
//   getBatteryLife_lm20_pt0_3955_kt1_rt1_300,
//   getBatteryLife_lm20_pt1_3955_kt1_rt1_300,
// } from './batteryLifeN54LM20'

// ===================== 类型 =====================

type BatteryLifeMethod = (
  id: number,
  modeID: SensorMode,
  rfStrengthSwitch: RfBoostSwitch,
  keyScanRate: KeyScanRate,
) => string

// ===================== 方法注册表 =====================

/**
 * 构建方法 key：sensorNum_kt_rt_battery_pt
 * 例如: "3398_0_0_800_pt0" 表示 3398传感器_机械微动_机械编码器_800mAh_多合一
 */
function buildKey(sensorNum: number, kt: number, rt: number, battery: number, pt: number): string {
  return `${sensorNum}_${kt}_${rt}_${battery}_pt${pt}`
}

/** L15 方法注册表 */
const L15_REGISTRY: Record<string, BatteryLifeMethod> = {
  [buildKey(3398, 0, 0, 800, 0)]: getBatteryLife_l15_pt0_3398_kt0_rt0_800,
  [buildKey(3398, 0, 0, 800, 1)]: getBatteryLife_l15_pt1_3398_kt0_rt0_800,
  [buildKey(3950, 1, 0, 800, 0)]: getBatteryLife_l15_pt0_3950_kt1_rt0_800,
  [buildKey(3950, 1, 0, 800, 1)]: getBatteryLife_l15_pt1_3950_kt1_rt0_800,
  [buildKey(3950, 0, 0, 800, 0)]: getBatteryLife_l15_pt0_3950_kt0_rt0_800,
  [buildKey(3950, 0, 0, 800, 1)]: getBatteryLife_l15_pt1_3950_kt0_rt0_800,
  [buildKey(3955, 1, 1, 800, 0)]: getBatteryLife_l15_pt0_3955_kt1_rt1_800,
  [buildKey(3955, 1, 1, 800, 1)]: getBatteryLife_l15_pt1_3955_kt1_rt1_800,
  [buildKey(3950, 1, 1, 300, 0)]: getBatteryLife_l15_pt0_3950_kt1_rt1_300,
  [buildKey(3950, 1, 1, 300, 1)]: getBatteryLife_l15_pt1_3950_kt1_rt1_300,
}

/** H20 方法注册表 */
const H20_REGISTRY: Record<string, BatteryLifeMethod> = {
  [buildKey(3950, 1, 1, 800, 0)]: getBatteryLife_h20_pt0_3950_kt1_rt1_800,
  [buildKey(3950, 1, 1, 800, 1)]: getBatteryLife_h20_pt1_3950_kt1_rt1_800,
  [buildKey(3955, 1, 1, 800, 0)]: getBatteryLife_h20_pt0_3955_kt1_rt1_800,
  [buildKey(3955, 1, 1, 800, 1)]: getBatteryLife_h20_pt1_3955_kt1_rt1_800,
  [buildKey(3950, 1, 1, 300, 0)]: getBatteryLife_h20_pt0_3950_kt1_rt1_300,
  [buildKey(3950, 1, 1, 300, 1)]: getBatteryLife_h20_pt1_3950_kt1_rt1_300,
}

/** L05 方法注册表 */
const L05_REGISTRY: Record<string, BatteryLifeMethod> = {
  [buildKey(3398, 0, 0, 800, 0)]: getBatteryLife_l05_pt0_3398_kt0_rt0_800,
  [buildKey(3398, 0, 0, 800, 1)]: getBatteryLife_l05_pt1_3398_kt0_rt0_800,
  [buildKey(3950, 0, 0, 800, 0)]: getBatteryLife_l05_pt0_3950_kt0_rt0_800,
  [buildKey(3950, 0, 0, 800, 1)]: getBatteryLife_l05_pt1_3950_kt0_rt0_800,
  [buildKey(3950, 1, 0, 800, 0)]: getBatteryLife_l05_pt0_3950_kt1_rt0_800,
  [buildKey(3950, 1, 0, 800, 1)]: getBatteryLife_l05_pt1_3950_kt1_rt0_800,
  [buildKey(3955, 1, 1, 800, 0)]: getBatteryLife_l05_pt0_3950_kt0_rt0_800,
  [buildKey(3955, 1, 1, 800, 1)]: getBatteryLife_l05_pt1_3950_kt0_rt0_800,
}

/** LM20 方法注册表 */
const LM20_REGISTRY: Record<string, BatteryLifeMethod> = {
  [buildKey(3955, 1, 1, 800, 0)]: getBatteryLife_l15_pt0_3955_kt1_rt1_800,
  [buildKey(3955, 1, 1, 800, 1)]: getBatteryLife_l15_pt1_3955_kt1_rt1_800,
  [buildKey(3955, 1, 1, 300, 0)]: getBatteryLife_l15_pt0_3950_kt1_rt1_300,
  [buildKey(3955, 1, 1, 300, 1)]: getBatteryLife_l15_pt1_3950_kt1_rt1_300,
}

/** MCU → 方法注册表 */
const MCU_REGISTRY: Record<string, Record<string, BatteryLifeMethod>> = {
  N54L15: L15_REGISTRY,
  N54H20: H20_REGISTRY,
  N54L05: L05_REGISTRY,
  N54LM20: LM20_REGISTRY,
}

// ===================== 辅助函数 =====================

/** 回报率协议值 → 续航表 id 索引 (0-6)，与芯片的回报率槽位下标一致 */
function reportRateToId(rate: ReportRate): number {
  const deviceStore = useDeviceStore()
  return getReportRateSlot(deviceStore.currentDevice?.features.mousePerfCfg, rate)
}

/** 传感器字符串 → 数字型号 */
function parseSensor(sensor: string): number {
  if (!sensor) return 0
  // 匹配 PAW3398 / PAW3950U / PAW3955U 等
  const match = sensor.match(/(\d{4})/)
  return match ? Number(match[1]) : 0
}

/** 微动类型字符串 → kt 索引 */
function parseMicroSwitch(val: string): number {
  return val === 'optical' ? 1 : 0
}

/** 编码器类型字符串 → rt 索引 */
function parseEncoder(val: string): number {
  return val === 'optical' ? 1 : 0
}

/**
 * 根据 manifest 查找对应的续航计算方法
 */
function findMethod(
  mcu: string,
  sensorNum: number,
  kt: number,
  rt: number,
  battery: number,
  pt: number,
): BatteryLifeMethod | null {
  const registry = MCU_REGISTRY[mcu]
  if (registry) {
    const key = buildKey(sensorNum, kt, rt, battery, pt)
    if (registry[key]) return registry[key]
  }

  return null
}

// ===================== 对外接口 =====================

/**
 * 统一续航计算入口
 *
 * 根据当前连接设备的 manifest 自动选择对应的续航表并计算续航时间。
 * 调用此方法前请确保设备已连接（currentDevice 不为 null）。
 *
 * @param reportRate    回报率
 * @param sensorMode    传感器模式
 * @param rfBoostSwitch RF 增强开关
 * @param keyScanRate   按键扫描率
 * @param rfPacketType   RF 发包类型：0 = 多合一(Turbo)，其他 = 1T1R
 */
export function getBatteryLife(
  reportRate: ReportRate,
  sensorMode: SensorMode,
  rfBoostSwitch: RfBoostSwitch,
  keyScanRate: KeyScanRate,
  rfPacketType: RfPacketType,
): string {
  const deviceStore = useDeviceStore()
  const manifest = deviceStore.currentDevice?.manifest
  if (!manifest) return '100h'

  const id = reportRateToId(reportRate)
  const pt = rfPacketType === 0 ? 0 : 1

  // 从 manifest 提取路由参数
  const mcu = manifest.MCU || ''
  const sensorNum = parseSensor(manifest.sensor || '')
  const kt = parseMicroSwitch(manifest.microSwitch || '')
  const rt = parseEncoder(manifest.encoder || '')
  const battery = manifest.battery || 800

  // 查找对应的方法
  const method = findMethod(mcu, sensorNum, kt, rt, battery, pt)
  if (method) {
    // 机械微动 无按键扫描率 默认传入 3
    if (kt === 0) {
      return method(id, sensorMode, rfBoostSwitch, 3)
    }
    return method(id, sensorMode, rfBoostSwitch, keyScanRate)
  }

  // 未找到匹配方法，返回默认值
  return '100h'
}
